import { Button, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { TableCard } from '../../../../components/Section';
import { IJadwaOrder } from '../../../../types/jadwa';
import { getJadwaFundInvestAmounts } from './helpers';
import { round2Format } from '../../../../utils/number';

interface JadwaFundInvestAmountsProps {
  orders: IJadwaOrder[];
  /** Opens the fund order's tab below. */
  onSelectFundOrder: (tabIndex: number) => void;
}

const sar = (value: number) => round2Format(value);

/**
 * What ops invests in each fund, after the FX spread and the transfer fee came off the wire — client and
 * satellite. All SAR, plus the fund's share of the USD sent for a USD fund. Straight from the rows
 * madkhol-jadwa's listener saved when it sized the wire.
 */
export function JadwaFundInvestAmounts({ orders, onSelectFundOrder }: JadwaFundInvestAmountsProps) {
  const rows = getJadwaFundInvestAmounts(orders);

  if (!rows.length) {
    return null;
  }

  const totals = rows.reduce(
    (sum, row) => ({
      gross: sum.gross + row.client.gross + row.satellite.gross,
      fx: sum.fx + row.client.fx + row.satellite.fx,
      fee: sum.fee + row.client.fee + row.satellite.fee,
      net: sum.net + row.totalNetSar,
    }),
    { gross: 0, fx: 0, fee: 0, net: 0 },
  );

  return (
    <TableCard
      title="Amount to invest per fund"
      description="After the FX spread and transfer fee. SAR unless marked; the last column is the fund's share of the wire in its currency."
      sx={{ mb: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fund Order</TableCell>
            <TableCell>Wire</TableCell>
            <TableCell align="right">Client Gross</TableCell>
            <TableCell align="right">FX</TableCell>
            <TableCell align="right">Fee</TableCell>
            <TableCell align="right">Client Net</TableCell>
            <TableCell align="right">Satellite Gross</TableCell>
            <TableCell align="right">Sat. FX</TableCell>
            <TableCell align="right">Sat. Fee</TableCell>
            <TableCell align="right">Satellite Net</TableCell>
            <TableCell align="right">To Invest (SAR)</TableCell>
            <TableCell align="right">To Invest (Wire)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.fundOrderId} hover>
              <TableCell>
                <Button size="small" onClick={() => onSelectFundOrder(row.tabIndex)} sx={{ minWidth: 0, px: 1 }}>
                  #{row.fundOrderId}
                </Button>
              </TableCell>
              <TableCell>
                {row.currency ?? '—'}
                {row.channel ? ` · ${row.channel}` : ''}
              </TableCell>
              <TableCell align="right">{sar(row.client.gross)}</TableCell>
              <TableCell align="right">{sar(row.client.fx)}</TableCell>
              <TableCell align="right">{sar(row.client.fee)}</TableCell>
              <TableCell align="right">{sar(row.client.net)}</TableCell>
              <TableCell align="right">{sar(row.satellite.gross)}</TableCell>
              <TableCell align="right">{sar(row.satellite.fx)}</TableCell>
              <TableCell align="right">{sar(row.satellite.fee)}</TableCell>
              <TableCell align="right">{sar(row.satellite.net)}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sar(row.totalNetSar)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {row.totalNetInCurrency != null ? `${sar(row.totalNetInCurrency)} ${row.currency ?? ''}` : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>Total</TableCell>
            <TableCell align="right" colSpan={6}>
              Gross {sar(totals.gross)} · FX {sar(totals.fx)} · Fee {sar(totals.fee)}
            </TableCell>
            <TableCell colSpan={2} />
            <TableCell align="right">{sar(totals.net)}</TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </TableCard>
  );
}
